<template>
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" class="relative z-10" @close="close">
            <TransitionChild as="template" enter="ease-in-out duration-500" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-500" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-hidden">
                <div class="absolute inset-0 overflow-hidden">
                    <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                        <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700" enter-from="translate-x-full" enter-to="translate-x-0" leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0" leave-to="translate-x-full">
                            <DialogPanel class="pointer-events-auto relative w-96">
                                <TransitionChild as="template" enter="ease-in-out duration-500" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-500" leave-from="opacity-100" leave-to="opacity-0">
                                    <div class="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4">
                                        <button type="button" class="rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" @click="close">
                                            <span class="sr-only">Close panel</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </TransitionChild>
                                <div class="h-full overflow-y-auto bg-white p-8">
                                    <div class="space-y-6 pb-16">
                                        <div>
                                            <div class="aspect-w-10 aspect-h-7 block w-full overflow-hidden rounded-lg">
                                                <img :src="visibleMedia.original_url" alt="" class="object-cover" />
                                            </div>
                                            <div class="mt-4 flex items-start justify-between">
                                                <div>
                                                    <h2 class="text-lg font-medium text-gray-900"><span class="sr-only">Details for </span>{{ visibleMedia.name }}</h2>
                                                    <p class="text-sm font-medium text-gray-500">3.9 MB</p>
                                                </div>
                                                <button type="button" class="ml-4 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                                    <span class="h-6 w-6" aria-hidden="true" />
                                                    <span class="sr-only">Favorite</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 class="font-medium text-gray-900">Information</h3>
                                            <dl class="mt-2 divide-y divide-gray-200 border-t border-b border-gray-200">
                                                <div class="flex justify-between py-3 text-sm font-medium">
                                                    <dt class="text-gray-500 flex items-center">Uploaded by</dt>
                                                    <dd class="text-gray-900 flex items-center space-x-2">
                                                        <img class="h-8 w-8 rounded-full object-cover " :src="visibleMedia.creator.profile_photo_url" alt="">
                                                        <span>{{ visibleMedia.creator.name }}</span>
                                                    </dd>
                                                </div>
                                                <div class="flex justify-between py-3 text-sm font-medium">
                                                    <dt class="text-gray-500">Uploaded at</dt>
                                                    <dd class="text-gray-900"><formatted-date :date="visibleMedia.created_at" /></dd>
                                                </div>
                                                <div v-if="visibleMedia.editor !== null" class="flex justify-between py-3 text-sm font-medium">
                                                    <dt class="text-gray-500 flex items-center">Last modified by</dt>
                                                    <dd class="text-gray-900 flex items-center space-x-2">
                                                        <img class="h-8 w-8 rounded-full object-cover " :src="visibleMedia.editor.profile_photo_url" alt="">
                                                        <span>{{ visibleMedia.editor.name }}</span>
                                                    </dd>
                                                </div>
                                                <div v-if="visibleMedia.updated_at !== null" class="flex justify-between py-3 text-sm font-medium">
                                                    <dt class="text-gray-500">Last modified at</dt>
                                                    <dd class="text-gray-900"><formatted-date :date="visibleMedia.updated_at" /></dd>
                                                </div>
                                                <div class="flex justify-between py-3 text-sm font-medium">
                                                    <dt class="text-gray-500">Size</dt>
                                                    <dd class="text-gray-900"><formatted-size :size="visibleMedia.size" /></dd>
                                                </div>
                                            </dl>
                                        </div>
                                        <div>
                                            <h3 class="font-medium text-gray-900">Description</h3>
                                            <div class="mt-2 flex items-center justify-between">
                                                <p class="text-sm italic text-gray-500">Add a description to this image.</p>
                                                <button type="button" class="-mr-2 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                                    <span class="h-5 w-5" aria-hidden="true" />
                                                    <span class="sr-only">Add description</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 class="font-medium text-gray-900">Connected to places</h3>
                                            <ul role="list" class="mt-2 divide-y divide-gray-200 border-t border-b border-gray-200">
                                                <li class="flex items-center justify-between py-3">
                                                    <div class="flex items-center">
                                                        <p class="text-sm italic text-gray-500">Not related to any places.</p>
                                                    </div>
                                                </li>
                                                <!--
                                                <li class="flex items-center justify-between py-3">
                                                    <div class="flex items-center">
                                                        <img src="https://images.unsplash.com/photo-1502685104226-ee32379fefbe?ixlib=rb-=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=3&w=1024&h=1024&q=80" alt="" class="h-8 w-8 rounded-full" />
                                                        <p class="ml-4 text-sm font-medium text-gray-900">Aimee Douglas</p>
                                                    </div>
                                                    <button type="button" class="ml-6 rounded-md bg-white text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Remove<span class="sr-only"> Aimee Douglas</span></button>
                                                </li>
                                                <li class="flex items-center justify-between py-3">
                                                    <div class="flex items-center">
                                                        <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixqx=oilqXxSqey&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="" class="h-8 w-8 rounded-full" />
                                                        <p class="ml-4 text-sm font-medium text-gray-900">Andrea McMillan</p>
                                                    </div>
                                                    <button type="button" class="ml-6 rounded-md bg-white text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Remove<span class="sr-only"> Andrea McMillan</span></button>
                                                </li>
                                                -->
                                            </ul>
                                        </div>
                                        <div class="flex">
                                            <button type="button" @click="save" class="flex-1 rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled" disabled>Download</button>
                                            <button type="button" @click="destroy" class="ml-3 flex-1 rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled" disabled>Delete</button>
                                        </div>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import {computed, ref, watch} from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import {useForm} from "@inertiajs/vue3";
import FormattedDate from "@/ComponentsDeprecated/Time/FormattedDate";
import FormattedSize from "@/ComponentsDeprecated/FormattedSize";

const emit = defineEmits('close')
const props = defineProps({
    media: {
        type: Object,
        default: {}
    }
})

const visibleMedia = ref(null)
const open = ref(false)
const form = useForm({})

watch(() => props.media, (media) => {
    if(Object.keys(media).length > 0) {
        visibleMedia.value = media;
        open.value = true;
    }
    else {
        open.value = false;
        setTimeout(() => {
            visibleMedia.value = null
        }, 1000)
    }
});

function save() {

}

function destroy() {

}

function close() {
    emit('close')
}

</script>
